(() => {
  'use strict';

  const NOTICE_ID = 'zeto-youquote-notice';

  const CART_QUOTE_BUTTON_SELECTOR = '#sc-shopify-load-submit-popup-cp';
  const CART_QUOTE_CONTAINER_SELECTOR = '.cp_raq_submit_btn_container';
  const CART_HANDLER_NAMESPACE = '.zetoRaqFix';

  // Kept exactly as written in the ticket.
  const NOTICE_TEXT =
    'Quotes may take up to 2 business day to get sent to you, and will not automatically trigger a delivery. ' +
    'The Zeto team will provide the quote to you and provide you with several delivery, order, and payment options.';

  let cartQuoteClickHandler = null;
  let cartCheckInProgress = false;
  let cartHandlerCaptureTimer = null;
  let cartHandlerCaptureAttempts = 0;
  let scanTimer;

  /**
   * Find the surrounding element for one custom field without accidentally
   * selecting the wrapper that contains every YouQuote custom field.
   */
  function findFieldWrapper(control, customFieldsContainer) {
    let current = control.parentElement;
    let candidate = current;

    while (current && current !== customFieldsContainer) {
      const controls = current.querySelectorAll('input, textarea, select');

      if (controls.length === 1) {
        candidate = current;
        current = current.parentElement;
      } else {
        break;
      }
    }

    return candidate || control.parentElement;
  }

  function dispatchFieldEvents(field) {
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function addInformationText(modal) {
    const footer = modal.querySelector('.sc-modal-footer');

    if (!footer) {
      return;
    }

    let notice = footer.querySelector(`#${NOTICE_ID}`);

    if (!notice) {
      notice = document.createElement('p');
      notice.id = NOTICE_ID;

      notice.style.margin = '0 0 16px';
      notice.style.textAlign = 'left';
      notice.style.fontSize = '14px';
      notice.style.lineHeight = '1.45';
      notice.style.color = '#333';

      // Prepending places it above the Submit button.
      footer.prepend(notice);
    }

    notice.textContent = NOTICE_TEXT;
  }

  function configureAddressFields(modal) {
    const customFieldsContainer = modal.querySelector(
      '#raq-popup-custom-fields-plc'
    );

    if (!customFieldsContainer) {
      return;
    }

    /*
     * Prefer the configured placeholders because they identify the fields
     * more safely than relying only on their current order.
     */
    const deliveryAddress =
      customFieldsContainer.querySelector(
        'textarea[placeholder="Enter Delivery Address"]'
      ) || customFieldsContainer.querySelectorAll('textarea')[0];

    const invoiceAddress =
      customFieldsContainer.querySelector(
        'textarea[placeholder="Enter Invoice Address"]'
      ) || customFieldsContainer.querySelectorAll('textarea')[1];

    const sameAddressCheckbox =
      customFieldsContainer.querySelector('input[type="checkbox"]');

    if (!deliveryAddress || !invoiceAddress || !sameAddressCheckbox) {
      return;
    }

    const invoiceWrapper = findFieldWrapper(
      invoiceAddress,
      customFieldsContainer
    );

    function updateInvoiceAddress() {
      if (sameAddressCheckbox.checked) {
        invoiceAddress.value = deliveryAddress.value;
        dispatchFieldEvents(invoiceAddress);

        /*
         * Do not disable the input. A disabled field would normally be
         * excluded from the submitted form data.
         */
        invoiceWrapper.hidden = true;
      } else {
        invoiceWrapper.hidden = false;
      }
    }

    /*
     * YouQuote can redraw the modal, so mark this particular checkbox after
     * binding the events to prevent duplicate listeners.
     */
    if (sameAddressCheckbox.dataset.zetoAddressBinding !== 'true') {
      sameAddressCheckbox.dataset.zetoAddressBinding = 'true';

      sameAddressCheckbox.addEventListener('change', updateInvoiceAddress);

      deliveryAddress.addEventListener('input', () => {
        if (sameAddressCheckbox.checked) {
          invoiceAddress.value = deliveryAddress.value;
          dispatchFieldEvents(invoiceAddress);
        }
      });
    }

    updateInvoiceAddress();
  }

  function configureYouQuoteModal() {
    const modals = document.querySelectorAll('.sc-modal-content');

    modals.forEach((modal) => {
      /*
       * Limit the customisation to the YouQuote submission modal rather than
       * changing every modal that happens to use the same CSS classes.
       */
      if (
        !modal.querySelector('#raq_comments') ||
        !modal.querySelector('#raq-popup-custom-fields-plc')
      ) {
        return;
      }

      addInformationText(modal);
      configureAddressFields(modal);
    });
  }

  /**
   * Return true only on the actual Shopify cart page.
   */
  function isCartPage() {
    return /^\/cart\/?$/.test(window.location.pathname);
  }

  /**
   * Capture YouQuote's own working cart click handler.
   *
   * YouQuote attaches the handler to .cp_raq_submit_btn_container.
   * Shopify replaces that container whenever the cart section is refreshed,
   * which destroys the handler.
   *
   * We move the same handler to <body>, which survives cart redraws.
   */
  function stabilizeCartQuoteClickHandler() {
    if (!isCartPage()) {
      return;
    }

    if (typeof ScJQuery_RAQ === 'undefined') {
      return;
    }

    const container = document.querySelector(
      CART_QUOTE_CONTAINER_SELECTOR
    );

    if (
      container &&
      typeof ScJQuery_RAQ._data === 'function'
    ) {
      const events = ScJQuery_RAQ._data(container, 'events');

      const clickHandler =
        events &&
        Array.isArray(events.click)
          ? events.click.find(
              (eventData) =>
                eventData.selector === CART_QUOTE_BUTTON_SELECTOR
            )
          : null;

      if (clickHandler && typeof clickHandler.handler === 'function') {
        cartQuoteClickHandler = clickHandler.handler;

        /*
         * Remove the original handler from the replaceable container.
         * Otherwise both the container handler and our body handler would
         * execute while the original cart DOM is still present.
         */
        ScJQuery_RAQ(container).off(
          'click',
          CART_QUOTE_BUTTON_SELECTOR,
          clickHandler.handler
        );
      }
    }

    if (!cartQuoteClickHandler) {
      return;
    }

    /*
     * Delegate the original YouQuote handler from <body>.
     * <body> is not replaced during Shopify's cart AJAX refresh.
     */
    ScJQuery_RAQ('body')
      .off(
        `click${CART_HANDLER_NAMESPACE}`,
        CART_QUOTE_BUTTON_SELECTOR
      )
      .on(
        `click${CART_HANDLER_NAMESPACE}`,
        CART_QUOTE_BUTTON_SELECTOR,
        cartQuoteClickHandler
      );
  }

  /**
   * YouQuote may initialise slightly after this custom script.
   * Retry briefly until its original cart handler becomes available.
   */
  function ensureCartHandlerCaptured() {
    if (!isCartPage() || cartQuoteClickHandler) {
      return;
    }

    stabilizeCartQuoteClickHandler();

    if (cartQuoteClickHandler) {
      return;
    }

    if (cartHandlerCaptureAttempts >= 40) {
      return;
    }

    if (cartHandlerCaptureTimer !== null) {
      return;
    }

    cartHandlerCaptureAttempts += 1;

    cartHandlerCaptureTimer = window.setTimeout(() => {
      cartHandlerCaptureTimer = null;
      ensureCartHandlerCaptured();
    }, 250);
  }

  /**
   * Restore the Request a Quote button after Shopify redraws the cart.
   *
   * We deliberately use YouQuote's own button-rendering function instead
   * of recreating its HTML ourselves.
   */
 async function restoreCartQuoteButton() {
  if (!isCartPage()) {
    return false;
  }

  if (document.querySelector(CART_QUOTE_BUTTON_SELECTOR)) {
    return true;
  }

  if (
    typeof window.sc_raq_app_global === 'undefined' ||
    typeof window.sc_raq_app_global
      .show_submit_quote_button_at_cart_page !== 'function'
  ) {
    return false;
  }

  if (cartCheckInProgress) {
    return false;
  }

  cartCheckInProgress = true;

    try {
      const root =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        return false;
      }

      const cart = await response.json();

      /*
      * Empty cart: Request a Quote must not be restored.
      */
      if (
        !cart ||
        !Array.isArray(cart.items) ||
        cart.items.length === 0
      ) {
        return false;
      }

      /*
      * Ask YouQuote itself to recreate the button.
      */
      if (!document.querySelector(CART_QUOTE_BUTTON_SELECTOR)) {
        window.sc_raq_app_global
          .show_submit_quote_button_at_cart_page();
      }

      /*
      * Restore/preserve its working click handler as well.
      */
      stabilizeCartQuoteClickHandler();

      return !!document.querySelector(
        CART_QUOTE_BUTTON_SELECTOR
      );
    } catch (error) {
      console.warn(
        '[Zeto YouQuote] Could not restore Request a Quote button:',
        error
      );

      return false;
    } finally {
      cartCheckInProgress = false;
    }
  }

  function runYouQuoteCustomizations() {
    configureYouQuoteModal();

    if (isCartPage()) {
      ensureCartHandlerCaptured();
      stabilizeCartQuoteClickHandler();
      restoreCartQuoteButton();
    }
  }

  /*
   * YouQuote injects its modal dynamically and Shopify redraws the cart
   * dynamically, after the original page has already loaded.
   *
   * One observer handles both behaviours.
   */
  const observer = new MutationObserver(() => {
    window.clearTimeout(scanTimer);

    scanTimer = window.setTimeout(
      runYouQuoteCustomizations,
      50
    );
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  runYouQuoteCustomizations();
})();